import { useState } from 'react'

import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Drawer } from '@/components/ui/Drawer'
import { useToast } from '@/components/ui/toastContext'
import type { AdminOrganization } from '@/features/admin/api/organizations.api'
import {
  useAdminOrganizationsQuery,
  useOrgHiresQuery,
  useOrgRecruitersQuery,
  useUpdateOrgStatus,
} from '@/features/admin/hooks/useAdminOrganizations'
import {
  useCreateOrgMapping,
  useDeleteOrgMapping,
  useOrgMappingsQuery,
  useToggleOrgMapping,
} from '@/features/admin/hooks/usePrograms'
import { getErrorMessage } from '@/utils/errors'
import { formatDate, humanizeEnum } from '@/utils/format'

const ORG_STATUSES = [
  { value: 'pending',     label: 'Pending',     badge: 'bg-warning-transparent text-warning',   icon: 'ti-clock' },
  { value: 'approved',    label: 'Approved',    badge: 'bg-success-transparent text-success',   icon: 'ti-circle-check' },
  { value: 'hold',        label: 'On Hold',     badge: 'bg-info-transparent text-info',         icon: 'ti-player-pause' },
  { value: 'rejected',    label: 'Rejected',    badge: 'bg-danger-transparent text-danger',     icon: 'ti-circle-x' },
  { value: 'blacklisted', label: 'Blacklisted', badge: 'bg-dark text-white',                    icon: 'ti-ban' },
]

const STATUS_MAP = Object.fromEntries(ORG_STATUSES.map((s) => [s.value, s]))

const USER_TYPE_BADGE: Record<string, string> = {
  recruiter_admin: 'bg-primary-transparent text-primary',
  recruiter_user:  'bg-secondary-transparent text-secondary',
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_MAP[status]
  if (!cfg) return <span className="badge bg-secondary-transparent fs-11">{status}</span>
  return (
    <span className={`badge fs-11 ${cfg.badge}`}>
      <i className={`ti ${cfg.icon} me-1`} />{cfg.label}
    </span>
  )
}

function OrgLmsMappingsSection({ org }: { org: AdminOrganization }) {
  const { data, isPending } = useOrgMappingsQuery(org.id)
  const createMapping = useCreateOrgMapping(org.id)
  const toggleMapping = useToggleOrgMapping(org.id)
  const deleteMapping = useDeleteOrgMapping(org.id)
  const toast = useToast()

  const isApproved = org.status === 'approved'

  return (
    <div className="mb-4 p-3 rounded border">
      <p className="fs-13 fw-semibold mb-1">
        <i className="ti ti-link me-1 text-primary" />
        LMS Mappings
      </p>
      <p className="fs-12 text-muted mb-3">
        Controls which LMS program phases this organisation participates in.
        {!isApproved && (
          <span className="text-warning ms-1">
            <i className="ti ti-alert-triangle me-1" />
            Mappings have no effect until the organisation is approved.
          </span>
        )}
      </p>

      {isPending ? (
        <div className="text-center py-2">
          <span className="spinner-border spinner-border-sm" />
        </div>
      ) : !data || data.lms_sources.length === 0 ? (
        <p className="fs-12 text-muted mb-0">No active LMS sources configured.</p>
      ) : (
        data.lms_sources.map((src) => (
          <div key={src.id} className="mb-3">
            <p className="fs-12 fw-semibold text-muted mb-2">
              {src.code}
              {src.code !== 'portal' && (
                <span
                  className={`ms-2 fw-normal ${src.phases_from_lms ? 'text-success' : 'text-warning'}`}
                  title={src.phases_from_lms ? 'Phases read live from the LMS' : 'LMS unreachable — showing the standard phase list'}
                >
                  <i className={`ti ${src.phases_from_lms ? 'ti-cloud-check' : 'ti-cloud-off'} me-1`} />
                  {src.phases_from_lms ? 'live from LMS' : 'standard list'}
                </span>
              )}
            </p>
            <div className="d-flex flex-wrap gap-2">
              {src.phases.map((ph) => (
                <div key={ph.academic_phase} className="d-flex align-items-center gap-1">
                  {ph.mapped && ph.mapping_id ? (
                    <>
                      <span className={`badge fs-11 ${ph.is_active ? 'bg-success-transparent text-success' : 'bg-secondary-transparent text-secondary'}`}>
                        {ph.academic_phase}
                      </span>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 fs-11"
                        title={ph.is_active ? 'Deactivate' : 'Activate'}
                        disabled={toggleMapping.isPending}
                        onClick={() => toggleMapping.mutate(ph.mapping_id!, {
                          onError: (err) => toast.error(getErrorMessage(err)),
                        })}
                      >
                        <i className={`ti ${ph.is_active ? 'ti-toggle-right text-success' : 'ti-toggle-left text-muted'} fs-16`} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-danger"
                        title="Remove mapping"
                        disabled={deleteMapping.isPending}
                        onClick={() => {
                          if (!confirm(`Remove ${ph.academic_phase} mapping from ${src.name}?`)) return
                          deleteMapping.mutate(ph.mapping_id!, {
                            onError: (err) => toast.error(getErrorMessage(err)),
                          })
                        }}
                      >
                        <i className="ti ti-x fs-12" />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm fs-11 py-0 px-2"
                      disabled={createMapping.isPending}
                      onClick={() => createMapping.mutate(
                        { lms_source_id: src.id, academic_phase: ph.academic_phase },
                        { onError: (err) => toast.error(getErrorMessage(err)) },
                      )}
                    >
                      <i className="ti ti-plus me-1" />
                      {ph.academic_phase}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

function OrgRecruitersDrawer({
  org,
  onClose,
}: {
  org: AdminOrganization
  onClose: () => void
}) {
  const { data: recruiters, isPending } = useOrgRecruitersQuery(org.id)
  const [showHires, setShowHires] = useState(false)
  const { data: hires, isPending: hiresLoading } = useOrgHiresQuery(org.id, showHires)
  const updateStatus = useUpdateOrgStatus()
  const toast = useToast()
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const handleStatusChange = (newStatus: string) => {
    setDropdownOpen(false)
    if (!confirm(`Change status to "${STATUS_MAP[newStatus]?.label}"?`)) return
    updateStatus.mutate({ orgId: org.id, status: newStatus }, {
      onSuccess: () => toast.success('Organisation status updated.'),
      onError: (err) => toast.error(getErrorMessage(err)),
    })
  }

  const currentStatus = STATUS_MAP[org.status]

  const drawerSubtitle = (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      {(() => {
        const cfg = STATUS_MAP[org.status]
        return cfg ? (
          <span className={`badge fs-11 ${cfg.badge}`}>
            <i className={`ti ${cfg.icon} me-1`} />{cfg.label}
          </span>
        ) : (
          <span className="badge bg-secondary-transparent fs-11">{org.status}</span>
        )
      })()}
      <span className="fs-12 text-muted">
        <i className="ti ti-calendar me-1" />Registered {formatDate(org.createdAt)}
      </span>
    </div>
  )

  return (
    <Drawer show onClose={onClose} title={org.name} subtitle={drawerSubtitle} width="lg">
      <div className="p-3">
        {/* Org summary stats */}
        <div className="d-flex align-items-center gap-2 mb-4 flex-wrap">
          <span className="badge bg-primary-transparent text-primary fs-12 px-3 py-2">
            <i className="ti ti-users me-1" />
            <strong>{org.recruiterCount}</strong> Recruiter{org.recruiterCount !== 1 ? 's' : ''}
          </span>
          <span className="badge bg-success-transparent text-success fs-12 px-3 py-2">
            <i className="ti ti-briefcase me-1" />
            <strong>{org.jobCount}</strong> Job{org.jobCount !== 1 ? 's' : ''} Posted
          </span>
          <span className="badge bg-warning-transparent text-warning fs-12 px-3 py-2">
            <i className="ti ti-user-check me-1" />
            <strong>{org.hiredCount}</strong> Hired
          </span>
        </div>

        {/* Status change */}
        <div className="mb-4 p-3 rounded border">
          <p className="fs-13 fw-semibold mb-2">
            <i className="ti ti-settings me-1 text-primary" />
            Change Organisation Status
          </p>
          <div className={`dropdown${dropdownOpen ? ' show' : ''}`} style={{ position: 'relative' }}>
            <button
              type="button"
              className={`btn btn-sm d-inline-flex align-items-center gap-1 fw-semibold ${currentStatus?.badge ?? 'btn-light'}`}
              onClick={() => setDropdownOpen((v) => !v)}
              disabled={updateStatus.isPending}
            >
              {updateStatus.isPending
                ? <span className="spinner-border spinner-border-sm" />
                : <i className={`ti ${currentStatus?.icon ?? 'ti-dots'}`} />
              }
              {currentStatus?.label ?? org.status}
              <i className="ti ti-chevron-down fs-12" />
            </button>
            {dropdownOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }} onClick={() => setDropdownOpen(false)} />
                <ul className="dropdown-menu show" style={{ zIndex: 1001, minWidth: 180 }}>
                  {ORG_STATUSES.filter((s) => {
                    if (s.value === org.status) return false
                    // approved → pending is irreversible
                    if (org.status === 'approved' && s.value === 'pending') return false
                    return true
                  }).map((s) => (
                    <li key={s.value}>
                      <button
                        type="button"
                        className="dropdown-item fs-13 d-flex align-items-center gap-2"
                        onClick={() => handleStatusChange(s.value)}
                      >
                        <span className={`badge ${s.badge}`}>
                          <i className={`ti ${s.icon}`} />
                        </span>
                        {s.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
          <p className="fs-12 text-muted mt-2 mb-0">
            Approved orgs can post jobs and access candidates. Rejected / Blacklisted orgs are blocked from the platform.
          </p>
        </div>

        {/* LMS Mappings */}
        <OrgLmsMappingsSection org={org} />

        {/* Hired Candidates */}
        <div className="mb-4 p-3 rounded border">
          <div
            className="d-flex align-items-center justify-content-between"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowHires((v) => !v)}
          >
            <p className="fs-13 fw-semibold mb-0">
              <i className="ti ti-user-check me-1 text-warning" />
              Hired Candidates
              {org.hiredCount > 0 && (
                <span className="badge bg-warning-transparent text-warning fs-11 ms-2">{org.hiredCount}</span>
              )}
            </p>
            <i className={`ti ${showHires ? 'ti-chevron-up' : 'ti-chevron-down'} text-muted`} />
          </div>

          {showHires && (
            <div className="mt-3">
              {hiresLoading ? (
                <div className="text-center py-3">
                  <span className="spinner-border spinner-border-sm" />
                </div>
              ) : (hires ?? []).length === 0 ? (
                <p className="text-muted fs-13 mb-0">No hired candidates yet.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th className="fs-12">Candidate</th>
                        <th className="fs-12">Job</th>
                        <th className="fs-12">LMS Program</th>
                        <th className="fs-12">Phase (FY)</th>
                        <th className="fs-12">Hired On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(hires ?? []).map((h, i) => (
                        <tr key={i}>
                          <td className="fs-13">{h.candidateName}</td>
                          <td className="fs-13 text-muted">{h.jobTitle}</td>
                          <td className="fs-12 text-muted">{h.lmsSource ?? '—'}</td>
                          <td className="fs-12">
                            {h.academicPhase ? (
                              <span className="badge bg-primary-transparent text-primary fs-11">{h.academicPhase}</span>
                            ) : '—'}
                          </td>
                          <td className="fs-12 text-muted">{h.hiredAt ? formatDate(h.hiredAt) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recruiters list */}
        <p className="fs-13 fw-semibold mb-2">
          <i className="ti ti-users me-1 text-primary" />
          Recruiters
        </p>

        {isPending ? (
          <div className="text-center py-4">
            <span className="spinner-border spinner-border-sm" />
          </div>
        ) : (recruiters ?? []).length === 0 ? (
          <p className="text-muted fs-13 text-center py-3">No recruiters in this organisation.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th className="fs-13">Name</th>
                  <th className="fs-13">Email</th>
                  <th className="fs-13">Role</th>
                  <th className="fs-13">Status</th>
                  <th className="fs-13">Joined</th>
                </tr>
              </thead>
              <tbody>
                {(recruiters ?? []).map((user) => (
                  <tr key={user.id} style={{ height: 48 }}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className="avatar avatar-sm avatar-rounded bg-primary-transparent fs-12 fw-semibold text-primary"
                          style={{ minWidth: 28, height: 28 }}
                        >
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                        <span className="fs-13 fw-medium">{user.name}</span>
                      </div>
                    </td>
                    <td className="fs-13 text-muted">{user.email}</td>
                    <td>
                      <span className={`badge fs-11 ${USER_TYPE_BADGE[user.userType] ?? 'bg-secondary-transparent'}`}>
                        {humanizeEnum(user.userType)}
                      </span>
                    </td>
                    <td>
                      <span className={`badge fs-11 ${
                        user.status === 'active' ? 'bg-success-transparent text-success' :
                        user.status === 'blocked' ? 'bg-danger-transparent text-danger' :
                        'bg-secondary-transparent text-secondary'
                      }`}>
                        {humanizeEnum(user.status)}
                      </span>
                    </td>
                    <td className="fs-12 text-muted">
                      {user.createdAt ? formatDate(user.createdAt) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Drawer>
  )
}

const ORG_COLUMNS: DataTableColumn<AdminOrganization>[] = [
  {
    key: 'name',
    header: 'Organisation',
    render: (org) => (
      <div className="d-flex align-items-center gap-2">
        <span
          className="avatar avatar-sm avatar-rounded bg-primary-transparent text-primary fs-13 fw-semibold"
          style={{ minWidth: 32, height: 32 }}
        >
          {org.name.charAt(0).toUpperCase()}
        </span>
        <span className="fs-13 fw-medium">{org.name}</span>
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (org) => <StatusBadge status={org.status} />,
  },
  {
    key: 'recruiterCount',
    header: 'Recruiters',
    className: 'text-center',
    render: (org) => (
      <span className="badge bg-primary-transparent text-primary fs-12">
        <i className="ti ti-users me-1" />{org.recruiterCount}
      </span>
    ),
  },
  {
    key: 'jobCount',
    header: 'Jobs Posted',
    className: 'text-center',
    render: (org) => (
      <span className="badge bg-success-transparent text-success fs-12">
        <i className="ti ti-briefcase me-1" />{org.jobCount}
      </span>
    ),
  },
  {
    key: 'hiredCount',
    header: 'Total Hired',
    className: 'text-center',
    render: (org) => (
      <span className="badge bg-warning-transparent text-warning fs-12">
        <i className="ti ti-user-check me-1" />{org.hiredCount}
      </span>
    ),
  },
  {
    key: 'createdAt',
    header: 'Registered',
    render: (org) => <span className="fs-12 text-muted">{formatDate(org.createdAt)}</span>,
  },
  {
    key: 'chevron',
    header: '',
    render: () => <i className="ti ti-chevron-right text-muted" />,
  },
]

export function AdminOrganizationsPage() {
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [selected, setSelected] = useState<AdminOrganization | null>(null)

  const { data, isPending } = useAdminOrganizationsQuery({
    q: q || undefined,
    status: statusFilter || undefined,
    page,
    per_page: 10,
  })
  const pagination = data?.pagination

  return (
    <>
      <div className="my-4 d-flex align-items-center justify-content-between flex-wrap gap-2 page-header-breadcrumb">
        <div>
          <p className="fw-semibold fs-20 mb-1">Organisations</p>
          <p className="text-muted fs-13 mb-0">
            {pagination ? `${pagination.total} organisations registered` : 'All recruiter organisations.'}
          </p>
        </div>
        <div style={{ minWidth: 220 }}>
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-white">
              <i className="ti ti-search fs-13 text-muted" />
            </span>
            <input
              type="text"
              className="form-control fs-13"
              placeholder="Search organisation…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1) }}
            />
          </div>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <button
          type="button"
          className={`btn btn-sm ${statusFilter === '' ? 'btn-primary' : 'btn-light'}`}
          onClick={() => { setStatusFilter(''); setPage(1) }}
        >
          All
        </button>
        {ORG_STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            className={`btn btn-sm ${statusFilter === s.value ? 'btn-primary' : 'btn-light'}`}
            onClick={() => { setStatusFilter(s.value); setPage(1) }}
          >
            <i className={`ti ${s.icon} me-1`} />{s.label}
          </button>
        ))}
      </div>

      <div className="card custom-card">
        <DataTable
          columns={ORG_COLUMNS}
          rows={data?.organizations ?? []}
          rowKey={(org) => org.id}
          loading={isPending}
          emptyMessage="No organisations found."
          pagination={pagination}
          onPageChange={(p) => setPage(p)}
          onRowClick={(org) => setSelected(org)}
        />
      </div>

      {selected && (
        <OrgRecruitersDrawer org={selected} onClose={() => setSelected(null)} />
      )}
    </>
  )
}
